import React, { useEffect, useMemo, useRef, useState } from "react";
import { getJson, postJson, errorMsg } from "../api";

export default function StepBomReview({ projectId, onSuccess }) {
  const AVAILABILITY_PROGRESS_MS = 120000;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [blockingCount, setBlockingCount] = useState(0);
  const [priced, setPriced] = useState(false);
  const [replaceRowId, setReplaceRowId] = useState(null);
  const [replaceMpnInput, setReplaceMpnInput] = useState("");
  const [needsReprice, setNeedsReprice] = useState(false);
  const [pendingRepriceIds, setPendingRepriceIds] = useState(new Set());
  const [resolvingItems, setResolvingItems] = useState(new Map());
  const [targetMpn, setTargetMpn] = useState(() => {
    const key = `pcb_replace_target_${projectId}`;
    const v = localStorage.getItem(key);
    if (v) localStorage.removeItem(key);
    return v || null;
  });
  const [autoPrice] = useState(() => {
    const key = `pcb_auto_price_${projectId}`;
    const v = localStorage.getItem(key);
    if (v) localStorage.removeItem(key);
    return !!v;
  });
  const targetRowRef = useRef(null);
  const autoLaunchRef = useRef(false);
  const progressIntervalsRef = useRef(new Map()); // Map<itemId, intervalId>
  const [progressMap, setProgressMap] = useState(new Map()); // Map<itemId, 0-100>
  const [globalProgress, setGlobalProgress] = useState(0);

  const startProgress = (itemId) => {
    setProgressMap((prev) => new Map([...prev, [itemId, 0]]));
    const interval = setInterval(() => {
      setProgressMap((prev) => {
        const current = prev.get(itemId) ?? 0;
        const next = current + Math.max(0.4, (85 - current) * 0.04);
        const capped = Math.min(next, 85);
        return new Map([...prev, [itemId, capped]]);
      });
    }, 120);
    progressIntervalsRef.current.set(itemId, interval);
  };

  const completeProgress = (itemId) => {
    const interval = progressIntervalsRef.current.get(itemId);
    if (interval) clearInterval(interval);
    progressIntervalsRef.current.delete(itemId);
    setProgressMap((prev) => new Map([...prev, [itemId, 100]]));
    setTimeout(() => {
      setProgressMap((prev) => {
        const next = new Map(prev);
        next.delete(itemId);
        return next;
      });
    }, 180);
  };

  const clean = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    if (!s) return "";
    if (s.toLowerCase() === "nan") return "";
    return s;
  };

  const pickDesignator = (it) =>
    clean(it?.Designator) ||
    clean(it?.designator) ||
    clean(it?.refdes) ||
    clean(it?.refDes) ||
    "";

  const pickBlockingReason = (it) => clean(it?.blocking_reason);
  const isNotAssembled = (it) => clean(it?.status) === "not_assembled";
  const isCustomerSupplied = (it) => clean(it?.status) === "customer_supplied";

  const normalizeMountType = (value) => {
    const raw = clean(value).toLowerCase();
    if (!raw) return "cms";
    if (raw === "pressfit") return "press_fit";
    if (["trad", "traversant", "tht", "through_hole", "through-hole"].includes(raw)) return "trad";
    if (raw === "press_fit") return "press_fit";
    return "cms";
  };
  const fetchSaved = async () => {
    setError("");
    setLoading(true);
    try {
      const data = await getJson(`/user/projects/${projectId}/bom-items/`);
      const items = data?.items || [];
      setItems(items);
      const savedBlockingCount = data?.blocking_count || 0;
      setBlockingCount(savedBlockingCount);
      const hasPricingData = items.some(
        (it) => it.price_unit != null || it.blocking_reason || it.status === "blocking"
      );
      setPriced(hasPricingData);
      return { ok: true, hasPricingData };
    } catch (e) {
      setError(errorMsg(e, "Erreur chargement BOM"));
      return { ok: false, hasPricingData: false };
    } finally {
      setLoading(false);
    }
  };
  const fetchPriced = async ({ continueIfClean = false } = {}) => {
    setError("");
    const startedAt = Date.now();
    setGlobalProgress(4);
    setLoading(true);
    const progressTimer = setInterval(() => {
      const elapsedRatio = Math.min((Date.now() - startedAt) / AVAILABILITY_PROGRESS_MS, 1);
      const curvedRatio = 1 - Math.pow(1 - elapsedRatio, 1.8);
      setGlobalProgress(Math.min(90, 4 + (curvedRatio * 86)));
    }, 1000);
    try {
      const data = await getJson(`/user/projects/${projectId}/bom-after-pricing/`);
      const blockingArr = data?.pricing_meta?.blocking || [];
      const blockingMap = new Map(
        blockingArr.map((b) => [String(b.mpn || "").trim(), String(b.reason || "").trim()])
      );

      const enriched = (data?.items || []).map((it) => {
        const mpn = String(it?.mpn || "").trim();
        return {
          ...it,
          blocking_reason: blockingMap.get(mpn) || it.blocking_reason || "",
        };
      });

      setItems(enriched);
      const nextBlockingCount = Number(data?.blocking_count || 0);
      setBlockingCount(nextBlockingCount);
      setPriced(true);
      setNeedsReprice(false);
      setPendingRepriceIds(new Set());
      if (continueIfClean && nextBlockingCount === 0) {
        onSuccess?.();
      }
      return { ok: true, blockingCount: nextBlockingCount };
    } catch (e) {
      setError(errorMsg(e, "Erreur recalcul stock/prix"));
      return { ok: false, blockingCount: null };
    } finally {
      clearInterval(progressTimer);
      setGlobalProgress(100);
      setLoading(false);
      setTimeout(() => setGlobalProgress(0), 260);
    }
  };

  useEffect(() => {
    autoLaunchRef.current = false;
    fetchSaved();
  }, [projectId]);

  useEffect(() => {
    if (!targetMpn || loading || items.length === 0) return;
    if (!targetRowRef.current) return;
    targetRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [targetMpn, loading, items]);

  useEffect(() => {
    if (loading) return;
    if (autoLaunchRef.current) return;
    const shouldAutoReprice = autoPrice || !priced || blockingCount > 0;
    if (!shouldAutoReprice) return;

    autoLaunchRef.current = true;
    fetchPriced();
  }, [loading, priced, blockingCount, autoPrice, projectId]);

  const patchFromPayload = (payload) => {
    const action = payload?.action;
    if (action === "replace") return { mpn: payload.replacement_mpn, status: "replaced", blocking_reason: "" };
    if (action === "customer_supplied") return { status: "customer_supplied", blocking_reason: "" };
    if (action === "not_assembled") return { status: "not_assembled", blocking_reason: "" };
    if (action === "mark_ok") return { status: "ok", blocking_reason: "" };
    if (action === "add_note") return {}; // note seule : ne pas toucher au statut ni au blocking_reason
    return {};
  };

  const resolve = async (it, payload) => {
    setError("");
    const action = payload?.action;
    setResolvingItems((prev) => new Map([...prev, [it.id, action]]));
    if (action === "replace") startProgress(it.id);
    try {
      const res = await postJson(`/user/projects/${projectId}/bom-items/${it.id}/resolve/`, payload);

      setItems((prev) => {
        if (action === "not_assembled") {
          return prev.filter((x) => x.id !== it.id);
        }

        const pricingPatch = res?.pricing
          ? {
              pricing_blocked: res.pricing.pricing_blocked,
              blocking_reason: res.pricing.blocking_reason,
              price_unit: res.pricing.price_unit,
            }
          : {};

        return prev.map((x) =>
          x.id === it.id
            ? {
                ...x,
                ...patchFromPayload(payload),
                ...(payload.note !== undefined ? { client_note: payload.note } : {}),
                ...(payload.mount_type ? { mount_type: payload.mount_type } : {}),
                ...pricingPatch,
              }
            : x
        );
      });

      return res;
    } catch (e) {
      setError(errorMsg(e, "Erreur action BOM"));
    } finally {
      setResolvingItems((prev) => {
        const next = new Map(prev);
        next.delete(it.id);
        return next;
      });
      if (action === "replace") completeProgress(it.id);
    }
  };

  const updateMountType = async (it, mountType) => {
    const nextMountType = normalizeMountType(mountType);
    setError("");
    try {
      await postJson(`/user/projects/${projectId}/bom-items/${it.id}/resolve/`, {
        action: "set_mount_type",
        mount_type: nextMountType,
      });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, mount_type: nextMountType } : x)));
    } catch (e) {
      setError(errorMsg(e, "Erreur mise à jour du type de montage"));
    }
  };

  const rowPriority = (it) => {
    if (priced && pickBlockingReason(it)) return 0; // rouge — en premier (haut)
    if (isCustomerSupplied(it)) return 1;            // orange — au milieu
    return 2;                                         // vert — en dernier
  };

  const sortedItems = useMemo(() => {
    const base = [...items]
      .filter((it) => !isNotAssembled(it));

    if (!priced) return base;

    base.sort((a, b) => {
      const pa = rowPriority(a);
      const pb = rowPriority(b);
      if (pa !== pb) return pa - pb;
      return clean(a.mpn).localeCompare(clean(b.mpn));
    });
    return base;
  }, [items, priced]);

  const isBlocked = (it) => (priced || !!it.pricing_blocked) && !!pickBlockingReason(it);
  const currentBlockingCount = useMemo(() => {
    if (!priced) return sortedItems.filter((it) => !!it.pricing_blocked && !!pickBlockingReason(it)).length;
    return sortedItems.filter((it) => !!pickBlockingReason(it)).length;
  }, [priced, sortedItems]);
  const hasBlockingInCurrentList = currentBlockingCount > 0;

  const rowStyle = (it) => {
    if (!priced && !it.pricing_blocked) return {};
    if (isBlocked(it)) return {
      background: "rgba(220, 38, 38, 0.22)",
      outline: "2px solid rgba(220, 38, 38, 0.7)",
      outlineOffset: "-1px",
    };
    if (pendingRepriceIds.has(it.id)) return {
      background: "rgba(202, 138, 4, 0.18)",
      outline: "2px solid rgba(202, 138, 4, 0.6)",
      outlineOffset: "-1px",
    };
    if (isCustomerSupplied(it)) return {
      background: "rgba(234, 88, 12, 0.18)",
    };
    return {
      background: "rgba(22, 163, 74, 0.18)",
    };
  };

  if (loading) return (
    <div className="stepper-section">
      <div className="bom-loading-card">
        <div className="bom-loading-title">Calcul disponibilité composants...</div>
        <div className="bom-loading-subtitle">Interrogation des fournisseurs et vérification des stocks. Cette étape peut durer environ 2 minutes.</div>
        <div className="bom-loading-progress" role="progressbar" aria-valuenow={Math.round(globalProgress || 12)} aria-valuemin="0" aria-valuemax="100">
          <div className="bom-loading-progress-fill" style={{ width: `${Math.max(12, Math.round(globalProgress || 12))}%` }} />
        </div>
        <div className="bom-loading-percent">{Math.max(12, Math.round(globalProgress || 12))}%</div>
      </div>
    </div>
  );

  return (
    <div className="stepper-section">
      <h2>2) Revue BOM</h2>

      {error && <div className="alert error">{error}</div>}

      {priced && (
        <div className={hasBlockingInCurrentList ? "alert error" : "alert ok"} style={{ marginBottom: 12 }}>
          {hasBlockingInCurrentList
            ? <><b>{currentBlockingCount} composant{currentBlockingCount > 1 ? "s" : ""} bloquant{currentBlockingCount > 1 ? "s" : ""}</b> : action requise avant de continuer.</>
            : <><b>Tous les composants sont disponibles</b>, vous pouvez continuer.</>
          }
        </div>
      )}

      {priced && (
        <div style={{ display: "flex", gap: 16, marginBottom: 8, fontSize: 13, flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: "rgba(22, 163, 74, 0.5)", display: "inline-block" }} />
            Disponible
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: "rgba(234, 88, 12, 0.45)", display: "inline-block" }} />
            Fourni par le client
          </span>
          {/* <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: "rgba(202, 138, 4, 0.45)", border: "2px solid rgba(202,138,4,0.7)", display: "inline-block" }} />
            MPN remplacé  disponibilité non vérifiée
          </span> */}
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: "rgba(220, 38, 38, 0.55)", border: "2px solid rgba(220,38,38,0.8)", display: "inline-block" }} />
            Indisponible : action requise
          </span>
        </div>
      )}

      <table className="table bom-review-table">
        <thead>
          <tr>
            <th>MPN</th>
            <th>Qté</th>
            <th>Désignateur</th>
            <th>Footprint</th>
            <th>Fabricant</th>
            <th>Montage</th>
            <th>Note</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((it) => {
            const mpn = clean(it.mpn);
            const isTarget = targetMpn && mpn.toLowerCase() === targetMpn.toLowerCase();
            const openReplace = () => {
              setReplaceRowId(it.id);
              setReplaceMpnInput(mpn);
            };
            const confirmReplace = async () => {
              const rep = clean(replaceMpnInput);
              if (!rep) return;
              const res = await resolve(it, {
                action: "replace",
                replacement_mpn: rep,
                mount_type: normalizeMountType(it.mount_type),
              });
              setReplaceRowId(null);
              setReplaceMpnInput("");
              if (isTarget) setTargetMpn(null);
              if (!res?.pricing) {
                setNeedsReprice(true);
                setPendingRepriceIds((prev) => new Set([...prev, it.id]));
              } else {
                setPendingRepriceIds((prev) => { const next = new Set(prev); next.delete(it.id); return next; });
              }
            };
            const resolvingAction = resolvingItems.get(it.id);
            const isResolving = !!resolvingAction;
            const resolvingLabel = {
              replace: "vérification MPN…",
              customer_supplied: "enregistrement…",
              not_assembled: "suppression…",
              mark_ok: "enregistrement…",
            }[resolvingAction] || "en cours…";
            return (
              <tr
                key={it.id}
                ref={isTarget ? targetRowRef : null}
                style={{
                  ...rowStyle(it),
                  ...(isResolving ? { opacity: 0.6, animation: "pulse 1s ease-in-out infinite" } : {}),
                }}
              >
                <td style={{ position: "relative", paddingBottom: progressMap.has(it.id) ? 10 : undefined }}>
                  {mpn}
                  {isResolving && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: "#f6c94a", fontStyle: "italic" }}>
                      {resolvingLabel}
                    </span>
                  )}
                  {progressMap.has(it.id) && (
                    <div style={{ position: "absolute", bottom: 2, left: 0, right: 0, height: 4, background: "rgba(99,102,241,0.15)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${Math.round(progressMap.get(it.id))}%`,
                        background: progressMap.get(it.id) >= 100 ? "#22c55e" : "#6366f1",
                        borderRadius: 2,
                        transition: progressMap.get(it.id) >= 100 ? "width 0.1s ease" : "width 0.12s linear",
                      }} />
                    </div>
                  )}
                </td>
                <td>{clean(it.qty)}</td>
                <td>{pickDesignator(it)}</td>
                <td>{clean(it.footprint)}</td>
                <td>{clean(it.manufacturer)}</td>
                <td className="bomMountCell">
                  <select
                    className="input bomMountSelect"
                    value={normalizeMountType(it.mount_type)}
                    onChange={(e) => updateMountType(it, e.target.value)}
                  >
                    <option value="cms">CMS</option>
                    <option value="trad">Traversant</option>
                    <option value="press_fit">Press-fit</option>
                  </select>
                </td>
                <td className="noteCell">
                  <button
                    className={`btn${clean(it.client_note) ? " hasNote" : ""}`}
                    title={clean(it.client_note) || undefined}
                    onClick={() => {
                      const note = window.prompt("Note :", clean(it.client_note));
                      if (note === null) return;
                      resolve(it, { action: "add_note", note, mount_type: normalizeMountType(it.mount_type) });
                    }}
                  >
                    {clean(it.client_note) ? "Note ajoutée" : "Ajouter note"}
                  </button>
                </td>
                <td className="actionsCell">
                  <div className="actionsWrap">
                    <div className="actionsTop">
                      <button
                        className={`btn ${isCustomerSupplied(it) ? "isActive" : ""}`}
                        disabled={isResolving}
                        onClick={() => resolve(it, { action: "customer_supplied", mount_type: normalizeMountType(it.mount_type) })}
                      >
                        {isCustomerSupplied(it) ? "Fourni client" : "Je fournis"}
                      </button>
                    </div>

                    <div className="actionsBottom">
                      {replaceRowId === it.id ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input
                            className="input"
                            style={{ width: 140 }}
                            value={replaceMpnInput}
                            onChange={(e) => setReplaceMpnInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") confirmReplace(); if (e.key === "Escape") { setReplaceRowId(null); setReplaceMpnInput(""); } }}
                            autoFocus
                            placeholder="Nouveau MPN"
                          />
                          <button className="btn primary" onClick={confirmReplace}>OK</button>
                          <button className="btn" onClick={() => { setReplaceRowId(null); setReplaceMpnInput(""); }}>✕</button>
                        </div>
                      ) : (
                        <button className={`btn${isTarget ? " primary" : ""}`} disabled={isResolving} onClick={openReplace}>
                          Remplacer MPN
                        </button>
                      )}

                      <button className="btn removeBtn" title="Ne pas assembler" disabled={isResolving} onClick={() => resolve(it, { action: "not_assembled" })}>
                        ✕
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn" onClick={fetchPriced} disabled={loading}>
          {loading ? "Calcul en cours…" : "Actualiser disponibilité"}
        </button>
        <button
          className="btn primary"
          disabled={loading || submitting || needsReprice}
          onClick={async () => {
            setSubmitting(true);
            try {
              if (!priced) {
                const result = await fetchPriced({ continueIfClean: true });
                if (!result?.ok) return;
                if ((result?.blockingCount || 0) > 0) {
                  setError("Il reste des composants bloquants. Corrige-les puis recalcule avant de continuer.");
                }
                return;
              }
              if (hasBlockingInCurrentList) {
                setError("Il reste des composants bloquants. Corrige-les puis recalcule avant de continuer.");
                return;
              }
              onSuccess?.();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? "Vérification…" : "Continuer"}
        </button>
        {needsReprice && !loading && (
          <span style={{ color: "#b45309", fontSize: 13 }}>
            MPN modifié — actualisez la disponibilité avant de continuer.
          </span>
        )}
      </div>
    </div>
  );
}
